import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { ErrorState } from '../../../../../components/ui/ErrorState';
import { BulkResult } from '../../../../../components/ui/BulkResult';
import {
  COURIER_CHANGE_STATUSES,
  changeCourierBulk,
  fetchAvailableCouriers,
} from '../../../../../features/attention/attentionActionThunks';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';

/**
 * Hands one or many shipments to another courier.
 *
 * One `PATCH` per shipment — there is no batch endpoint — so the outcome is a
 * tally, not a yes or no. It stays open on that tally instead of closing,
 * because two of the four outcomes are things the operator has to know about:
 * shipments skipped for their status, and shipments that had already left.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Array<{delivery_id: number, status: string}>} props.rows
 * @param {() => void} [props.onSuccess]
 */
export const ChangeCourierModal = ({ isOpen, onClose, rows = [], onSuccess }) => {
  const dispatch = useDispatch();
  const { couriers } = useSelector((state) => state.attention);
  const loadingCouriers = useSelector(selectIsRunning(fetchAvailableCouriers));

  const [courier, setCourier] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen) dispatch(fetchAvailableCouriers());
  }, [isOpen, dispatch]);

  // The backend returns objects keyed by `courier`, and the value it expects
  // back is that same string.
  const courierOptions = useMemo(
    () => couriers.map((item) => ({ value: item.courier, label: item.courier })),
    [couriers]
  );

  const eligible = rows.filter((row) => COURIER_CHANGE_STATUSES.includes(row.status));
  const skipped = rows.length - eligible.length;

  const handleClose = () => {
    if (saving) return;
    setCourier('');
    setError(null);
    setResult(null);
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const outcome = await dispatch(changeCourierBulk({ rows, courier })).unwrap();
      setResult(outcome);
      if (outcome.succeeded.length > 0) onSuccess?.();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudo cambiar el courier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cambiar Courier" busy={saving}>
      {result ? (
        <BulkResult result={result} skippedLabel="Por su estado" onClose={handleClose} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Se aplicará a {eligible.length} {eligible.length === 1 ? 'envío' : 'envíos'}.
          </p>

          {/* Said before pressing, not after: the operator picked N rows and
              only some of them can move. */}
          {skipped > 0 && (
            <p className="text-xs text-warning text-center -mt-3">
              {skipped} {skipped === 1 ? 'quedará fuera' : 'quedarán fuera'} porque su estado ya no
              permite cambiar de courier.
            </p>
          )}

          {error && <ErrorState variant="inline" title="No se pudo aplicar" description={error} />}

          <FilterableSelect
            label="Nuevo courier"
            variant="form"
            value={courier}
            onChange={setCourier}
            options={courierOptions}
            // Locked while saving: the courier on screen has to be the one being applied.
            disabled={loadingCouriers || saving}
            placeholder={loadingCouriers ? 'Cargando couriers...' : 'Selecciona un courier'}
          />

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleSave}
              loading={saving}
              loadingLabel="Cambiando..."
              disabled={!courier || eligible.length === 0}
            >
              Cambiar courier
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
